import { createSlice } from '@reduxjs/toolkit';
import { Params } from 'react-router-dom';
import ability from './config/acl';
import abilityMapping from './config/acl/ability-mapping';
import { routeMeta } from './config/router/key-routes-mapping';
import { primaryMenuItems } from './layouts/layouts.constants';
import { iAppAuthType, iAppInitialStore, iAppUserType, iHeaderProps, iPrimaryNavbarItem, iRouteKeys, iRouteMetaObject, iSecondaryNavBarActionType, iSecondaryNavbar, iSecondaryNavbarChild } from './types/app.types';
import { deepCopy, setLocalStorageItem, updateUserPermissions } from './utils';

const filterSecondaryNavbar = (secondaryNavbarItems: iSecondaryNavbarChild[]) => {
  return secondaryNavbarItems.filter((el) => {
    if (el.children?.length) {
      const filteredChildren = filterSecondaryNavbar(el.children) as typeof el.children;
      el.children = filteredChildren;
      return !!el.children?.length;
    }
    if (el.checkIsPermitted) {
      return el.checkIsPermitted();
    }
    return el.permissions.some((permission) => ability.can(permission.action, permission.subject));
  });
};

const initialState: iAppInitialStore = {
  isBootLoaded: false,
  domain: null,
  activeRoute: null,
  layout: {
    primaryNavbarItems: [],
    secondaryNavbar: null,
    secondaryNavbarHeader: null,
    activeSecNavItems: [],
  },
  auth: null,
  user: null,
  bootApiError: false,
  omnifulCities: null,
  pusherLoading: false,
  blocker: null,
};

type iFetchBootResponse = {
  user: iAppUserType
  tenant: {
    type: string //'FLEET_PARTNER' // ['FLEET_PARTNER', 'FLEET_CLIENT']
  }
  user_plan: {
    //
  }
} | null

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setActiveRoute: (state, { payload }: { payload: iRouteMetaObject & { key: iRouteKeys } }) => {
      state.activeRoute = payload;
    },
    setPrimaryNavbarItems: (state, { payload }: { payload: iPrimaryNavbarItem[] }) => {
      state.layout.primaryNavbarItems = payload;
    },
    setActiveSecNavItems: (state, { payload }) => {
      state.layout.activeSecNavItems.push(payload);
    },
    removeActiveSecNavItems: (state, { payload }) => {
      state.layout.activeSecNavItems.splice(payload, 1);
    },
    appInit: (state, action: { payload: iFetchBootResponse }) => {
      if (action.payload?.user) {
        const user = action.payload.user;
        const roles = user.roles || [];
        const userPermissions = updateUserPermissions(roles);
        // state.freshDeskAuthData = action.payload?.freshDeskAuthData
        user.permissions = userPermissions;
        ability.update([...userPermissions, { action: abilityMapping.module.view.action, subject: abilityMapping.module.view.subject }]);

        state.user = user;
        if (state.auth) {
          state.auth.permissions = userPermissions;
        }
        state.isBootLoaded = true;
        // initRealTime(user.id)
      } else {
        state.bootApiError = true; 
      }
    },
    appReset: (state) => {
      state.activeRoute = null,
      state.layout = {
        primaryNavbarItems: [],
        secondaryNavbar: null,
        secondaryNavbarHeader: null,
        activeSecNavItems: [],
      }
    },
    login: (state, action: { payload: iAppAuthType }) => {
      if (action.payload) {
        state.auth = action.payload;
        // Only store auth data in localStorage if access_token exists (login complete)
        if (action.payload.access_token) {
          const { ...restAuthData } = action.payload || {};
          // state.getLimitExceedInfo = log_out_info
          setLocalStorageItem('auth', restAuthData);
          // window.localStorage.setItem('auth', JSON.stringify(restAuthData))
        }
      }
    },
    clearAuthData: (state) => {
      state.auth = null;
    },
    setSecondaryNavBar: (state, action: { payload: { routeKey: iRouteKeys; actionType: iSecondaryNavBarActionType; params?: Readonly<Params<string>> } }) => {
      const primaryMenuId = routeMeta[action.payload.routeKey].primaryNavId;
      const secondaryNavbarTemp = primaryMenuItems.find((el) => el.id === primaryMenuId)?.secondaryNavbar;
      const secondaryNavbar = deepCopy(secondaryNavbarTemp) as iSecondaryNavbar;
      const filteredNavbarChildren = filterSecondaryNavbar(secondaryNavbar?.children || []);
      if (secondaryNavbar?.children)  secondaryNavbar.children = filteredNavbarChildren;
      if (action.payload.routeKey && action.payload.actionType === 'filterParams' && action.payload.params) {
        if (secondaryNavbar?.children?.length) {
          secondaryNavbar.children = secondaryNavbar.children.map((elem) => {
            for (const key in action.payload.params) {
              if (action.payload.params[key]) return {...elem, navLink: elem.navLink.replace(`:${key}`, action.payload.params[key] as string)};
            }
            return elem;
          });
        }
        if (secondaryNavbar)  state.layout.secondaryNavbar = secondaryNavbar as iSecondaryNavbar;
      }
      else if (action.payload.actionType === 'cleanupSidebar') {
        state.layout.secondaryNavbar = null;
      } else if (action.payload.actionType === 'setSecondaryNavBar') {
        state.layout.secondaryNavbar = secondaryNavbar as iSecondaryNavbar;
      }
    },
    setSecondaryNavBarHeader: (state, action: { payload: { Header: React.FC<iHeaderProps> | null, doCleanup: boolean } }) => {
      if (action.payload.doCleanup) {
        state.layout.secondaryNavbarHeader = null;
      } else {
        state.layout.secondaryNavbarHeader = action.payload.Header;
      }
    },
    omnifulCities: (state, { payload }: { payload: any }) => {
      state.omnifulCities = payload;
    },
    setPusherLoading: (state, { payload }: { payload: boolean }) => {
      state.pusherLoading = payload;
    },
    setBlocker: (state, { payload }: { payload: unknown }) => {
      state.blocker = payload;
    },
  },
});

export const { setPrimaryNavbarItems, appInit, appReset, login, clearAuthData, setActiveRoute, omnifulCities, setActiveSecNavItems, removeActiveSecNavItems, setSecondaryNavBar, setSecondaryNavBarHeader, setPusherLoading, setBlocker } = appSlice.actions;
export default appSlice.reducer;
